import { useEffect, useMemo, useState } from "react";
import AdminTopbar from "../../components/topbars/AdminTopbar";
import { IconSearch } from "../../components/icons/IconsAdmin";
import {
    getObrasSociales,
    crearObraSocial,
    agregarPlanAObraSocial,
    getPlanes,
} from "../../api/apiResources";
import "./ObrasSociales.css";

const normalizeObraSocial = (os) => ({
    id: os._id || os.id,
    nombre: os.nombre || "Sin nombre",
    codigo: os.codigo || "",
    activa: os.activa !== false,
    planes: Array.isArray(os.planes) ? os.planes : [],
});

// ─── Fila expandible por obra social ───────────────────────────────────────

const ObraSocialRow = ({ obraSocial, planesDisponibles, onPlanAgregado }) => {
    const [expanded, setExpanded] = useState(false);
    const [asignando, setAsignando] = useState(false);
    const [planSeleccionado, setPlanSeleccionado] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    // IDs de planes ya asignados (pueden venir como objetos o strings)
    const planesAsignados = obraSocial.planes.map((p) => String(p._id || p.id || p));

    // Planes que todavía no están asignados a esta obra social
    const planesDisponiblesParaAsignar = planesDisponibles.filter(
        (p) => !planesAsignados.includes(String(p.id))
    );

    const handleAgregarPlan = async () => {
        if (!planSeleccionado) return setError("Seleccioná un plan.");
        setSaving(true);
        setError("");
        try {
            await agregarPlanAObraSocial(obraSocial.id, planSeleccionado);
            setPlanSeleccionado("");
            setAsignando(false);
            onPlanAgregado();
        } catch (e) {
            setError(e.message || "Error al agregar el plan.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={`os-row${expanded ? " os-row--expanded" : ""}`}>
            <div className="os-row__header" onClick={() => setExpanded((v) => !v)}>
                <div className="os-row__info">
                    <span className="os-row__nombre">{obraSocial.nombre}</span>
                    {obraSocial.codigo && (
                        <span className="os-row__codigo">{obraSocial.codigo}</span>
                    )}
                    <span className="os-row__planes-count">
                        {obraSocial.planes.length} plan{obraSocial.planes.length !== 1 ? "es" : ""}
                    </span>
                    <span className={`os-row__estado ${obraSocial.activa ? "os-row__estado--activa" : "os-row__estado--inactiva"}`}>
                        {obraSocial.activa ? "Activa" : "Inactiva"}
                    </span>
                </div>
                <div className="os-row__chevron">{expanded ? "▲" : "▼"}</div>
            </div>

            {expanded && (
                <div className="os-row__body">
                    {obraSocial.planes.length === 0 && (
                        <p className="os-row__empty">Sin planes asignados todavía.</p>
                    )}

                    {obraSocial.planes.length > 0 && (
                        <ul className="os-planes-lista">
                            {obraSocial.planes.map((p, i) => {
                                const nombre =
                                    p.nombre ||
                                    planesDisponibles.find((pd) => String(pd.id) === String(p._id || p.id || p))?.nombre ||
                                    "–";
                                return (
                                    <li key={i} className="os-planes-lista__item">
                                        <span className="os-plan-badge">📋 {nombre}</span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    {error && <p className="os-row__error">{error}</p>}

                    {asignando ? (
                        <div className="os-row__add-form">
                            <p className="os-row__edit-title">Agregar plan</p>
                            {planesDisponiblesParaAsignar.length === 0 ? (
                                <p className="os-row__empty">Todos los planes ya están asignados.</p>
                            ) : (
                                <div className="os-assign-form">
                                    <label className="form-field">
                                        <span>Plan</span>
                                        <select
                                            value={planSeleccionado}
                                            onChange={(e) => setPlanSeleccionado(e.target.value)}
                                        >
                                            <option value="">Seleccioná un plan...</option>
                                            {planesDisponiblesParaAsignar.map((p) => (
                                                <option key={p.id} value={p.id}>{p.nombre}</option>
                                            ))}
                                        </select>
                                    </label>
                                </div>
                            )}
                            <div className="os-row__add-form-actions">
                                <button
                                    className="btn-ghost"
                                    onClick={() => { setAsignando(false); setError(""); }}
                                >
                                    Cancelar
                                </button>
                                {planesDisponiblesParaAsignar.length > 0 && (
                                    <button
                                        className="primary-button"
                                        onClick={handleAgregarPlan}
                                        disabled={saving}
                                    >
                                        {saving ? "Guardando..." : "Agregar plan"}
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <button
                            className="btn-ghost os-row__add-btn"
                            onClick={() => { setAsignando(true); setError(""); }}
                        >
                            + Agregar plan
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

// ─── Página principal ───────────────────────────────────────────────────────

const ObrasSociales = () => {
    const [obrasSociales, setObrasSociales] = useState([]);
    const [planes, setPlanes] = useState([]);
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // Estado del formulario de creación
    const [creando, setCreando] = useState(false);
    const [nuevoNombre, setNuevoNombre] = useState("");
    const [nuevaDescripcion, setNuevaDescripcion] = useState("");
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState("");

    const loadAll = async () => {
        setLoading(true);
        setError("");
        try {
            const [osData, planesData] = await Promise.all([getObrasSociales(), getPlanes()]);
            setObrasSociales(osData.map(normalizeObraSocial));
            setPlanes(planesData.map((p) => ({ id: p._id || p.id, nombre: p.nombre })));
        } catch (e) {
            setError(e.message || "Error al cargar datos.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadAll(); }, []);

    const filtradas = useMemo(() =>
            obrasSociales.filter((os) =>
                os.nombre.toLowerCase().includes(query.toLowerCase())
            ),
        [obrasSociales, query]
    );

    const handleCrear = async () => {
        if (!nuevoNombre.trim()) return setSaveError("Ingresá un nombre para la obra social.");
        if (!nuevaDescripcion.trim()) return setSaveError("Ingresá una descripción.");
        setSaving(true);
        setSaveError("");
        try {
            await crearObraSocial({ nombre: nuevoNombre.trim(), descripcion: nuevaDescripcion.trim() });
            setNuevoNombre("");
            setNuevaDescripcion("");
            setCreando(false);
            await loadAll();
        } catch (e) {
            setSaveError(e.message || "Error al crear la obra social.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="os-page">
            <AdminTopbar
                title="Obras sociales"
                subtitle={`${obrasSociales.length} obra${obrasSociales.length !== 1 ? "s" : ""} social${obrasSociales.length !== 1 ? "es" : ""} configurada${obrasSociales.length !== 1 ? "s" : ""}`}
                actionLabel="+ Nueva obra social"
                onAction={() => { setCreando(true); setSaveError(""); }}
            />

            <div className="os-page__body">
                <div className="os-search">
                    <IconSearch />
                    <input
                        type="text"
                        placeholder="Buscar obra social..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                </div>

                {creando && (
                    <div className="os-create-form">
                        <div className="form-row">
                            <label className="form-field">
                                <span>Nombre</span>
                                <input
                                    type="text"
                                    value={nuevoNombre}
                                    onChange={(e) => setNuevoNombre(e.target.value)}
                                    placeholder="Ej: OSDE"
                                    autoFocus
                                />
                            </label>
                        </div>
                        <div className="form-row">
                            <label className="form-field">
                                <span>Descripción</span>
                                <input
                                    type="text"
                                    value={nuevaDescripcion}
                                    onChange={(e) => setNuevaDescripcion(e.target.value)}
                                    placeholder="Ej: Obra social para empleados del sector privado"
                                />
                            </label>
                        </div>
                        {saveError && <p className="os-create-form__error">{saveError}</p>}
                        <div className="os-create-form__actions">
                            <button
                                className="btn-ghost"
                                onClick={() => { setCreando(false); setSaveError(""); }}
                            >
                                Cancelar
                            </button>
                            <button
                                className="primary-button"
                                onClick={handleCrear}
                                disabled={saving}
                            >
                                {saving ? "Guardando..." : "Crear obra social"}
                            </button>
                        </div>
                    </div>
                )}

                {loading && (
                    <div className="os-loading">
                        <div className="patient-state__loader" />
                        <p>Cargando obras sociales...</p>
                    </div>
                )}

                {!loading && error && <p className="os-error">{error}</p>}

                {!loading && !error && filtradas.length === 0 && (
                    <p className="os-empty">
                        {query
                            ? "No hay obras sociales que coincidan con la búsqueda."
                            : "No hay obras sociales creadas todavía."}
                    </p>
                )}

                {!loading && !error && (
                    <div className="os-lista">
                        {filtradas.map((os) => (
                            <ObraSocialRow
                                key={os.id}
                                obraSocial={os}
                                planesDisponibles={planes}
                                onPlanAgregado={loadAll}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ObrasSociales;